import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import FocusTrapping from "../../__hoc/FocusTrapping";
import { Button } from "../_controls";
import Alert from "../Alert/Alert";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";
import "./dialog.scss";
import { Cookie } from "../../../services/storage";
import { REDIRECT_URI, REQUEST_NUMBER } from "../../../constants/storage";
import { ROUTES } from "../../../configs/routes";
import { useTranslation } from "react-i18next";

/**
 * Props for the Dialog component.
 *
 * @description The Dialog component is a simple alert dialog with a title, body text,
 * and a single action button. It supports both regular dialogs and idle timeout dialogs.
 *
 * @property {string} id - HTML id attribute for the dialog container. Required.
 * @property {string} title - The title text displayed in the dialog header. Required.
 * @property {string} body - The body text displayed in the dialog content. Required.
 * @property {string} [buttonTitle="Ok"] - The text displayed on the action button.
 * @property {boolean} show - Whether the dialog is visible. Required.
 * @property {boolean} [isIdleDialog=false] - Whether this is an idle timeout dialog.
 *   When true, performs cleanup of session data and handles visibility changes.
 * @property {() => void} handleClose - Function called when the dialog is closed. Required.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic dialog
 * <Dialog
 *   id="confirm-dialog"
 *   title="Confirmation"
 *   body="Your changes have been saved successfully."
 *   show={isOpen}
 *   handleClose={() => setIsOpen(false)}
 * />
 *
 * @example
 * // Dialog with custom button text
 * <Dialog
 *   id="error-dialog"
 *   title="Error"
 *   body="An error occurred while processing your request."
 *   buttonTitle="Dismiss"
 *   show={showError}
 *   handleClose={() => setShowError(false)}
 * />
 *
 * @example
 * // Idle timeout dialog
 * <Dialog
 *   id="idle-dialog"
 *   title="Session Expired"
 *   body="Your session has expired due to inactivity."
 *   buttonTitle="Return to Home"
 *   show={isIdle}
 *   isIdleDialog={true}
 *   handleClose={handleIdleClose}
 * />
 */
interface Props {
	id: string;
	title: string;
	body: string;
	buttonTitle?: string;
	show: boolean;
	isIdleDialog?: boolean;
	handleClose: () => void;
	testId?: string;
}

/**
 * A simple alert dialog component with title, body, and action button.
 *
 * @description Dialog renders a modal dialog using the Alert component as a base.
 * It provides a simple interface for displaying messages with a single action button.
 * The component supports focus trapping for accessibility and integrates with the
 * application's dark mode theme.
 *
 * Features:
 * - Focus trapping for accessibility
 * - Dark mode support via Redux state
 * - Idle timeout dialog support with session cleanup
 * - Visibility change detection for idle dialogs
 * - Close button with customizable icon
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A dialog with title, body, and action button
 *
 * @see Props for all available properties
 * @see Alert for the underlying alert component
 */
export const Dialog: React.FC<Props> = (props) => {
	const { t } = useTranslation();
	const {
		id,
		title,
		body,
		show = false,
		buttonTitle = "Ok",
		isIdleDialog = false,
		handleClose,
		testId,
	} = props;
	const { isDarkMode } = useSelector<ApplicationState, LayoutState>(
		(state) => state.layout
	);

	const [isOpen, setIsOpen] = useState<boolean>(show);
	const handleOnClose = () => {
		handleClose();
		setIsOpen(false);
	};

	const cleanupFunctionForIdleUser = () => {
		window.history.replaceState(null, "", ROUTES.ROOT);
		Cookie.remove(REQUEST_NUMBER);
		Cookie.remove(REDIRECT_URI);
	};

	useEffect(() => {
		if (show) {
			setIsOpen(true);
			if (isIdleDialog) {
				cleanupFunctionForIdleUser();
			}
		}
		[isOpen, show];
	});

	const closeComponent = (viewType: string) => (
		<button
			className={viewType}
			data-testid="close-btn"
			onClick={handleOnClose}
		>
			<Svg
				icon={
					!isDarkMode
						? SVG_ICONS.DIALOG_CLOSE_BUTTON_ICON
						: SVG_ICONS.DIALOG_CLOSE_BUTTON_ICON_HC
				}
				width={18}
				height={18}
				title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
			/>
		</button>
	);
	return (
		<FocusTrapping isActive={isOpen} onHandleClose={handleOnClose} modalId={id}>
			<div data-testid={testId} className="dialog" id={id}>
				<Alert
					show={isOpen}
					title={title}
					customClose={closeComponent("dialog__close")}
					isDialogModal={true}
				>
					<div data-testid="dialog-container" className="dialog__text">
						<Typography variant="body2" className="subText">
							{body}
						</Typography>
					</div>

					<Alert.Footer>
						<div className="button-container">
							<Button
								className="dialog-ok-button"
								testId="dialog-ok-button"
								title={buttonTitle}
								onClick={handleOnClose}
								id="dialog-ok-button"
							></Button>
						</div>
					</Alert.Footer>
				</Alert>
			</div>
		</FocusTrapping>
	);
};
